import { isDefined, isString } from '@sgme/fp';
import { addMinutes, format, formatISO, isValid, type Locale, parseISO } from 'date-fns';
import { de, enUS as en, fr } from 'date-fns/locale';

import { type ISODate, NAMED_CALENDAR_TENORS, type StringDateTime } from '@/types/date.ts';

export type Timestamp = number; // = Date.now() or date.valueOf()

// useful ?
export function toUtc(date: Date) {
  return addMinutes(date, date.getTimezoneOffset());
}

export function formatISOToUtc(date: Date | string) {
  const dateToFormat = isString(date) ? parseISO(date) : date;
  return format(toUtc(dateToFormat), "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
}

export function formatFullISO(date: Date | string) {
  const dateToFormat = isString(date) ? parseISO(date) : date;

  if (!isValid(dateToFormat)) {
    throw new Error(`${String(date)} is not a valid date input for formatFullISO`);
  }

  return format(dateToFormat, "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'");
}

export function formatStringDateTime(date: Date): StringDateTime {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
}

export const getDateNow = () => new Date();

// => '2019-09-18'
export const getTodayDateOnly = () => formatISO(new Date(), { representation: 'date' });

const locales: { [key: string]: Locale } = {
  en,
  fr,
  de,
};

function formatWithLocaleWith(formatString: string) {
  return (date: Date | null | undefined, locale: string): string =>
    !isDefined(date) || !isValid(date) ? '' : format(date, formatString, { locale: locales[locale] });
}

export const FULL_DATE_FORMAT_TEXT = 'dd MMM yyyy h:mm a';
export const DATE_FORMAT_TEXT = 'dd MMM yyyy';

export const formatDateTimeWithLocale = formatWithLocaleWith(FULL_DATE_FORMAT_TEXT);

export const isTenor = (dateOrTenor: string) =>
  /^\d+[mdyw]$/gi.test(dateOrTenor.trim()) ||
  (NAMED_CALENDAR_TENORS as readonly string[]).includes(dateOrTenor.trim().toUpperCase());

export const isIsoDate = (value: string): value is ISODate => /\d\d\d\d-\d\d-\d\d/.test(value);

export function formatUserInputtedDate(date: Date | string | null, locale: string): string {
  if (date === null) {
    return '';
  }
  if (typeof date === 'string' && isTenor(date)) {
    return date;
  }
  const parsedDate = typeof date === 'string' ? parseISO(date) : date;
  if (isValid(parsedDate)) {
    return format(parsedDate, DATE_FORMAT_TEXT, { locale: locales[locale] });
  }
  return date as string;
}
